import * as t from 'io-ts';

import {makeApiType, makeEnumRuntimeType} from './apiTypes';

/*
 * Runtime type.
 */

export enum ConferenceTypesEnum {
  ZOOM = 'zoom',
  EVENT_HANGOUT = 'eventHangout',
  EVENT_NAMED_HANGOUT = 'eventNamedHangout',
  HANGOUTS_MEET = 'hangoutsMeet',
  SKYPE_FOR_BUSINESS = 'skypeForBusiness',
  SKYPE_FOR_CONSUMER = 'skypeForConsumer',
  TEAMS_FOR_BUSINESS = 'teamsForBusiness',
  GOOGLE_ADD_ON = 'googleAddOn',
}

export enum ConferenceEntryPointTypesEnum {
  VIDEO = 'video',
  PHONE = 'phone',
  SIP = 'sip',
  MORE = 'more',
}

export enum ConferenceStatusesEnum {
  SUCCESS = 'success',
  PENDING = 'pending',
  FAILURE = 'failure',
}

const RConferenceEntryPoint = t.readonly(
  t.type({
    type: makeEnumRuntimeType(ConferenceEntryPointTypesEnum),
    uri: t.string,
    label: t.union([t.string, t.undefined]),
    pin: t.union([t.string, t.undefined]),
    accessCode: t.union([t.string, t.undefined]),
    meetingCode: t.union([t.string, t.undefined]),
    passcode: t.union([t.string, t.undefined]),
    password: t.union([t.string, t.undefined]),
  }),
);

export const RConference = t.readonly(
  t.type({
    type: makeEnumRuntimeType(ConferenceTypesEnum),
    status: makeEnumRuntimeType(ConferenceStatusesEnum),
    id: t.union([t.string, t.undefined]),
    name: t.union([t.string, t.undefined]),
    description: t.union([t.string, t.undefined]),
    entryPoints: t.readonlyArray(RConferenceEntryPoint),
  }),
);

/*
 * Type.
 */

export interface ConferenceModel extends t.TypeOf<typeof RConference> {}
export interface ConferenceEntryPointModel extends t.TypeOf<typeof RConferenceEntryPoint> {}

/*
 * IO methods.
 */

export function parseConference(src: any): ConferenceModel {
  return {
    type: src.type,
    status: src.status,
    id: src.id || undefined,
    name: src.name || undefined,
    description: src.description || undefined,
    entryPoints: (src.entryPoints || []).map(parseConferenceEntryPoint),
  };
}

function parseConferenceEntryPoint(src: any): ConferenceEntryPointModel {
  return {
    type: src.type,
    uri: src.uri,
    label: src.label || undefined,
    pin: src.pin || undefined,
    accessCode: src.accessCode || undefined,
    meetingCode: src.meetingCode || undefined,
    passcode: src.passcode || undefined,
    password: src.password || undefined,
  };
}

export function parseLegacyConference(src: any): ConferenceModel {
  return {
    type: src.type,
    status: ConferenceStatusesEnum.SUCCESS,
    id: undefined,
    name: undefined,
    description: src.description,
    entryPoints: [
      {
        type: ConferenceEntryPointTypesEnum.VIDEO,
        uri: src.url,
        label: undefined,
        pin: undefined,
        accessCode: undefined,
        meetingCode: undefined,
        passcode: undefined,
        password: undefined,
      },
    ],
  };
}

export function serializeConference(conference: ConferenceModel) {
  return {
    type: conference.type,
    status: conference.status,
    id: conference.id,
    name: conference.name,
    description: conference.description,
    entryPoints: conference.entryPoints.map((p) => ({
      type: p.type,
      uri: p.uri,
      label: p.label || null,
      pin: p.pin || null,
      accessCode: p.accessCode || null,
      meetingCode: p.meetingCode || null,
      passcode: p.passcode || null,
      password: p.password || null,
    })),
  };
}

/*
 * API type.
 */

export const ApiLegacyConference = makeApiType('legacyConference', RConference, parseLegacyConference);
